import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Deck } from '@digital-twins/deck-ui/slides/contracts'
import { toast } from 'sonner'
import i18n from '@/i18n'
import { collectQualRespondentNames } from '@/features/studies/deck/render/enrichDeckRespondentImages'
import {
  planStudyReportDeckHydration,
  prepareStudyReportDeck,
  type StudyReportDeckHydrationInput,
} from '../deckHydration'
import type { RespondentProfile } from '../audienceProfiles'
import { resolvePopulationPersonaImages } from '../resolvePopulationPersonaImages'
import { studyReportClient } from '../studyReportClient'

export interface UseStudyReportDeckHydrationOptions {
  studyId: string
  study: StudyReportDeckHydrationInput | null
  audienceProfiles?: Array<RespondentProfile>
  responses?: Array<unknown>
  populationId?: string
}

type DeckLoadRequest = {
  studyId: string
  promise: Promise<boolean>
}

export function useStudyReportDeckHydration({
  studyId,
  study,
  audienceProfiles,
  responses,
  populationId,
}: UseStudyReportDeckHydrationOptions) {
  const [deck, setDeck] = useState<Deck | null>(null)
  const [deckLoading, setDeckLoading] = useState(false)
  const [populationImageMap, setPopulationImageMap] = useState<Map<string, string>>(
    () => new Map(),
  )
  const activeStudyIdRef = useRef(studyId)
  const loadDeckInFlightRef = useRef<DeckLoadRequest | null>(null)
  activeStudyIdRef.current = studyId

  const currentStudy =
    study && (!study.id || study.id === studyId) ? study : null

  useEffect(() => {
    setDeck(null)
    loadDeckInFlightRef.current = null
  }, [studyId])

  const currentDeck =
    deck && deck.experimentId === studyId ? deck : null

  const prepareDeck = useCallback(
    (nextDeck: Deck, coverImageUrl?: string) =>
      prepareStudyReportDeck(nextDeck, {
        coverImageUrl,
        coverBackgroundSeed: studyId,
        studyId,
        audienceProfiles,
        responses,
        populationImageMap,
      }),
    [audienceProfiles, populationImageMap, responses, studyId],
  )

  const updateDeck = useCallback(
    (nextDeck: Deck | null) => {
      setDeck(nextDeck ? prepareDeck(nextDeck) : null)
    },
    [prepareDeck],
  )

  // Load deck data from storage-backed API; returns whether slides are available.
  const loadDeck = useCallback(async (): Promise<boolean> => {
    if (
      Array.isArray(currentDeck?.slides) &&
      currentDeck.slides.length > 0
    ) {
      return true
    }
    if (loadDeckInFlightRef.current?.studyId === studyId) {
      return loadDeckInFlightRef.current.promise
    }

    let request!: DeckLoadRequest
    const requestStudyId = studyId
    const coverImageUrl = currentStudy?.coverImageUrl
    const run = async (): Promise<boolean> => {
      try {
        setDeckLoading(true)
        const deckData = await studyReportClient.getDeck(requestStudyId)
        if (activeStudyIdRef.current !== requestStudyId) {
          return false
        }
        if (
          deckData &&
          Array.isArray(deckData.slides) &&
          deckData.slides.length > 0
        ) {
          setDeck(prepareDeck(deckData as Deck, coverImageUrl))
          return true
        }
        return false
      } catch (error) {
        console.error('Failed to load deck:', error)
        toast.error(i18n.t('studyReport.deckLoadFailed', { ns: 'app' }))
        return false
      } finally {
        if (activeStudyIdRef.current === requestStudyId) {
          setDeckLoading(false)
        }
        if (loadDeckInFlightRef.current === request) {
          loadDeckInFlightRef.current = null
        }
      }
    }

    const promise = run()
    request = { studyId: requestStudyId, promise }
    loadDeckInFlightRef.current = request
    return promise
  }, [currentDeck, currentStudy?.coverImageUrl, prepareDeck, studyId])

  // Storage-backed reports often expose only a deck JSON key. Hydrate local
  // deck state so landing previews update as soon as the deck exists.
  useEffect(() => {
    const plan = planStudyReportDeckHydration(currentStudy, currentDeck, {
      coverBackgroundSeed: studyId,
      studyId,
    })
    if (plan.kind === 'noop') return
    if (plan.kind === 'inline') {
      setDeck(plan.deck)
      return
    }
    void loadDeck()
  }, [currentStudy, currentDeck, loadDeck, studyId])

  useEffect(() => {
    setPopulationImageMap(new Map())
  }, [studyId, populationId])

  // Stable string key of qual-analysis respondent names. Adding imageUrl to
  // respondent objects (via the re-enrichment effect) does not change name/label,
  // so this key stays identical after enrichment and prevents a redundant API round-trip.
  const qualRespondentNamesKey = useMemo(
    () =>
      currentDeck
        ? collectQualRespondentNames(currentDeck).sort().join('|')
        : '',
    [currentDeck],
  )

  useEffect(() => {
    if (!populationId || !qualRespondentNamesKey) {
      return
    }

    const names = qualRespondentNamesKey.split('|')
    let cancelled = false
    void resolvePopulationPersonaImages(populationId, names).then((nextMap) => {
      if (cancelled || nextMap.size === 0) {
        return
      }
      setPopulationImageMap((previous) => {
        if (
          previous.size === nextMap.size &&
          Array.from(nextMap.entries()).every(
            ([key, value]) => previous.get(key) === value,
          )
        ) {
          return previous
        }
        return nextMap
      })
    })

    return () => {
      cancelled = true
    }
  }, [populationId, qualRespondentNamesKey])

  // Re-apply respondent portrait enrichment when lookup sources load after hydration.
  useEffect(() => {
    setDeck((existing) => {
      if (!existing || existing.experimentId !== studyId) {
        return existing
      }
      return prepareDeck(existing, currentStudy?.coverImageUrl)
    })
  }, [
    audienceProfiles,
    populationImageMap,
    responses,
    currentStudy?.coverImageUrl,
    prepareDeck,
    studyId,
  ])

  return {
    deck: currentDeck,
    setDeck: updateDeck,
    deckLoading,
    loadDeck,
  }
}
